import { ModelBase } from './base';

/**
 * External platform a {@link SocialIdentity} belongs to.
 *
 * The string values are **persisted** — they are embedded in the mapping doc id
 * (see `socialIdentityKey` in `@psl/domains/social-identity`), so renaming one
 * orphans every existing mapping.
 */
export enum SocialProvider {
  instagram = 'instagram',
  facebook = 'facebook',
}

/**
 * Mapping from an external (Meta) identity to OUR user id — the seam that keeps
 * the Firebase uid independent of any platform's id space.
 *
 * Doc id is the natural key `${provider}_${appId}_${externalId}`, so resolution
 * is a `get` (no composite index) and first-write-wins is a plain `create`.
 *
 * `appId` is part of the key on purpose: Meta hands out **app-scoped** ids
 * (ASID / IGSID), so the same human has a different `externalId` per Meta app,
 * and "which app minted this id" is not recoverable after the fact.
 *
 * The mapping is meant to be permanent — it is what makes re-signing-up with the
 * same social account return the same creator instead of a duplicate. Removing
 * one (`SocialIdentityService.unlink`) is a support operation for a genuinely
 * mis-bound account, not what happens when a creator disconnects a channel
 * (channel state belongs to ECHO).
 *
 * The provider profile fields are a last-seen snapshot for support and debugging
 * only — nothing keys off them.
 */
export interface SocialIdentity extends ModelBase {
  provider: SocialProvider;
  /** Meta app the `externalId` is scoped to. */
  appId: string;
  /** The provider-side user id. Never used as, or embedded in, our user id. */
  externalId: string;
  /** Our Firebase uid. The whole point of this collection. */
  userId: string;
  username?: string;
  displayName?: string;
  email?: string;
  /**
   * True for rows written by the `backfillSocialIdentities` job: `userId` is a
   * legacy `ig_<id>` / `fb_<id>` uid that was derived from `externalId` before
   * this collection existed. New sign-ups never set it.
   */
  legacyUid?: boolean;
}
